import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { ConfirmDialog } from './ui/confirm-dialog';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(true);
  const signingInRef = useRef(false);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <div className="min-h-screen bg-muted/30" aria-hidden />
        <ConfirmDialog
          open={dialogOpen}
          onOpenChange={(open) => {
            if (!open && !signingInRef.current) {
              setDialogOpen(false);
              navigate('/', { replace: true });
            }
          }}
          title={t('auth.gateDialog.title')}
          description={t('auth.gateDialog.description')}
          confirmText={t('auth.gateDialog.confirm')}
          cancelText={t('auth.gateDialog.cancel')}
          variant="info"
          icon={<Lock className="h-5 w-5" strokeWidth={2} />}
          onConfirm={() => {
            signingInRef.current = true;
            setDialogOpen(false);
            navigate('/sign-in', { state: { from: location }, replace: true });
          }}
        />
      </>
    );
  }

  return <>{children}</>;
}
